import { create } from 'zustand';
import { Resource, ResourceCategoryRecord } from '@/types';
import { validateFileSize } from '@/lib/upload';
import { fetchStrapiCollection } from '@/lib/strapi';

const baseUrl = '/api/strapi';

interface ResourceCreatePayload {
  title: string;
  shortDescription: string;
  resourceLink?: string | null;
  phone?: string | null;
  /** Canonical slugs. Resolved to documentIds inside createResource. */
  categorySlugs: string[];
  isFeatured?: boolean;
  icon?: string;
  /**
   * MWBI-900. Send a Spanish field ONLY when the admin actually edited it: echoing back
   * untouched Spanish would diverge the fingerprint the CMS stores and silently latch this
   * resource to manual translation for good.
   */
  titleEs?: string;
  shortDescriptionEs?: string;
  /** Clearing this is the "Reset to automatic" signal. */
  translationLocked?: boolean;
}

interface ResourceUpdatePayload {
  title?: string;
  shortDescription?: string;
  // null clears the field, undefined leaves it untouched, string sets it.
  resourceLink?: string | null;
  phone?: string | null;
  /** `undefined` leaves the relation untouched; `[]` clears it. */
  categorySlugs?: string[];
  isFeatured?: boolean;
  icon?: string;
}

async function uploadResourceLogo(file: File): Promise<number> {
  validateFileSize(file);

  const formData = new FormData();
  formData.append('files', file);

  const uploadRes = await fetch(`${baseUrl}/upload`, {
    method: 'POST',
    body: formData,
  });

  if (!uploadRes.ok) {
    throw new Error('Failed to upload logo');
  }

  const uploadJson: Array<{ id?: number }> = await uploadRes.json();
  const id = uploadJson[0]?.id;
  if (typeof id !== 'number') {
    throw new Error('Logo upload returned no media id');
  }
  return id;
}

interface ResourceStore {
  resources: Resource[];
  featuredResources: Resource[];
  /** Available resource-categories (canonical list from api::resource-category). */
  categories: ResourceCategoryRecord[];
  categoriesLoaded: boolean;
  loading: boolean;
  selectedCategory: string | null;
  searchQuery: string;
  fetchResources: () => Promise<void>;
  fetchFeaturedResources: () => Promise<void>;
  fetchCategories: (force?: boolean) => Promise<void>;
  setSelectedCategory: (category: string | null) => void;
  setSearchQuery: (query: string) => void;
  // logoFile semantics: undefined = no change, File = upload + set, null = clear.
  createResource: (data: ResourceCreatePayload, logoFile?: File | null) => Promise<Resource | null>;
  updateResource: (documentId: string, data: ResourceUpdatePayload, logoFile?: File | null) => Promise<Resource | null>;
  deleteResource: (documentId: string) => Promise<boolean>;
}

export const useResourceStore = create<ResourceStore>((set, get) => ({
  resources: [],
  featuredResources: [],
  categories: [],
  categoriesLoaded: false,
  loading: false,
  selectedCategory: null,
  searchQuery: '',

  fetchCategories: async (force = false) => {
    if (!force && get().categoriesLoaded) return;
    try {
      const rows = await fetchStrapiCollection<ResourceCategoryRecord>('resource-categories', {
        fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived', 'resourceTopic', 'resourcePopulation'],
        sort: ['order:asc', 'name:asc'],
      });
      set({ categories: rows, categoriesLoaded: true });
    } catch (error) {
      console.error('Error fetching resource categories:', error);
    }
  },

  fetchResources: async () => {
    set({ loading: true });
    try {
      const allResources = await fetchStrapiCollection<Resource>('resources', {
        filters: { deletedAt: { $null: true } },
        populate: {
          logo: true,
          categories: { fields: ['id', 'documentId', 'name', 'slug', 'order', 'archived'] },
        },
        sort: 'createdAt:desc',
      });

      // Sort resources: Featured first (most recent first), then normal resources
      const sortedResources = allResources.sort((a, b) => {
        // If both are featured or both are not featured, sort by createdAt (most recent first)
        if (a.isFeatured === b.isFeatured) {
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        // Featured resources come first
        return a.isFeatured ? -1 : 1;
      });

      set({ resources: sortedResources });
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      set({ loading: false });
    }
  },

  fetchFeaturedResources: async () => {
    try {
      // eslint-disable-next-line local/no-unpaginated-strapi-fetch -- featured subset, intentionally limited to 10
      const res = await fetch(
        `${baseUrl}/resources?filters[isFeatured][$eq]=true&populate[logo]=true&populate[categories][fields][0]=id&populate[categories][fields][1]=documentId&populate[categories][fields][2]=name&populate[categories][fields][3]=slug&pagination[pageSize]=10`
      );
      if (res.ok) {
        const data = await res.json();
        set({ featuredResources: data.data || [] });
      }
    } catch (error) {
      console.error('Error fetching featured resources:', error);
    }
  },

  setSelectedCategory: (category) => set({ selectedCategory: category }),

  setSearchQuery: (query) => set({ searchQuery: query }),

  createResource: async (data, logoFile) => {
    try {
      // Resolve canonical slugs → documentIds via the store-loaded list.
      // Lazy-fetch if the caller hasn't already loaded the list.
      if (data.categorySlugs.length > 0 && !get().categoriesLoaded) {
        await get().fetchCategories();
      }
      const categoryDocumentIds = data.categorySlugs
        .map((slug) => get().categories.find((c) => c.slug === slug)?.documentId)
        .filter((docId): docId is string => Boolean(docId));

      const { categorySlugs: _slugs, ...rest } = data;
      void _slugs;
      const payload: Record<string, unknown> = { ...rest };
      if (categoryDocumentIds.length > 0) {
        payload.categories = { connect: categoryDocumentIds };
      }
      if (logoFile instanceof File) {
        payload.logo = await uploadResourceLogo(logoFile);
      } else if (logoFile === null) {
        payload.logo = null;
      }

      const res = await fetch(`${baseUrl}/resources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });
      if (res.ok) {
        const result = await res.json();
        await get().fetchResources();
        return result.data;
      }
    } catch (error) {
      console.error('Error creating resource:', error);
    }
    return null;
  },

  updateResource: async (documentId, data, logoFile) => {
    try {
      const { categorySlugs, ...rest } = data;
      const payload: Record<string, unknown> = { ...rest };
      if (categorySlugs !== undefined) {
        if (categorySlugs.length > 0 && !get().categoriesLoaded) {
          await get().fetchCategories();
        }
        const docIds = categorySlugs
          .map((slug) => get().categories.find((c) => c.slug === slug)?.documentId)
          .filter((docId): docId is string => Boolean(docId));
        payload.categories = { set: docIds };
      }
      if (logoFile instanceof File) {
        payload.logo = await uploadResourceLogo(logoFile);
      } else if (logoFile === null) {
        payload.logo = null;
      }

      const res = await fetch(`${baseUrl}/resources/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: payload }),
      });
      if (res.ok) {
        const result = await res.json();
        await get().fetchResources();
        return result.data;
      }
    } catch (error) {
      console.error('Error updating resource:', error);
    }
    return null;
  },

  deleteResource: async (documentId) => {
    try {
      // Soft delete: set deletedAt so live fetches stop returning it
      const res = await fetch(`${baseUrl}/resources/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { deletedAt: new Date().toISOString() } }),
      });
      if (res.ok) {
        await get().fetchResources();
        return true;
      }
    } catch (error) {
      console.error('Error deleting resource:', error);
    }
    return false;
  },
}));

